import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function RentalDialog({ open, onOpenChange, editing, onDone }) {
    const [name, setName] = useState("");
    const [startDate, setStartDate] = useState(todayISO());
    const [durationDays, setDurationDays] = useState("7");
    const [warningDays, setWarningDays] = useState("2");
    const [cost, setCost] = useState("0");
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setName(editing.name);
            setStartDate(editing.start_date);
            setDurationDays(String(editing.duration_days));
            setWarningDays(String(editing.warning_days ?? 3));
            setCost(String(editing.cost ?? 0));
            setNote(editing.note || "");
        } else {
            setName("");
            setStartDate(todayISO());
            setDurationDays("7");
            setWarningDays("2");
            setCost("0");
            setNote("");
        }
    }, [open, editing]);

    const submit = async () => {
        if (!name.trim()) return toast.error("Nom requis");
        const dur = parseInt(durationDays, 10);
        const warn = parseInt(warningDays, 10);
        if (!dur || dur <= 0) return toast.error("Durée invalide");
        if (isNaN(warn) || warn < 0) return toast.error("Délai d'alerte invalide");
        setLoading(true);
        try {
            const payload = {
                name,
                start_date: startDate,
                duration_days: dur,
                warning_days: warn,
                cost: parseFloat(cost) || 0,
                note,
            };
            if (editing) {
                await api.patch(`/rentals/${editing.id}`, payload);
                toast.success("Location mise à jour");
            } else {
                await api.post("/rentals", payload);
                toast.success("Location créée");
            }
            onOpenChange(false);
            onDone && onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
        setLoading(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="rental-dialog" className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">
                        {editing ? "Modifier la location" : "Nouvelle location"}
                    </DialogTitle>
                    <DialogDescription>
                        Configurez une location du club, entrepôt ou planque avec un rappel
                        avant expiration.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                            Nom
                        </Label>
                        <Input
                            data-testid="rental-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Ex : Loyer club, Bail entrepôt, Planque…"
                            className="mt-2 h-11"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Date de début
                            </Label>
                            <Input
                                data-testid="rental-start-input"
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="mt-2 h-11"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Durée (jours)
                            </Label>
                            <Input
                                data-testid="rental-duration-input"
                                type="number"
                                min="1"
                                value={durationDays}
                                onChange={(e) => setDurationDays(e.target.value)}
                                className="mt-2 h-11"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Alerte (jours avant)
                            </Label>
                            <Input
                                data-testid="rental-warning-input"
                                type="number"
                                min="0"
                                value={warningDays}
                                onChange={(e) => setWarningDays(e.target.value)}
                                className="mt-2 h-11"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Coût
                            </Label>
                            <Input
                                data-testid="rental-cost-input"
                                type="number"
                                min="0"
                                step="0.01"
                                value={cost}
                                onChange={(e) => setCost(e.target.value)}
                                className="mt-2 h-11"
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                            Note
                        </Label>
                        <Textarea
                            data-testid="rental-note-input"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={2}
                            className="mt-2"
                            placeholder="Propriétaire, contact, conditions…"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        data-testid="rental-cancel-btn"
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={loading}
                        data-testid="rental-submit-btn"
                        className="bg-[#3D4E2E] hover:bg-[#2E3A22] text-white"
                    >
                        {loading ? "Enregistrement…" : editing ? "Enregistrer" : "Créer"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
